//@author: devin

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { LynxService } from "../../services/lynx.service";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { EventService } from "../../services/event.service";
import { AlertService } from "../../services/alert.service";
import { TelemetryService } from "../../services/telemetry.service";
import { LynxWs } from "../../pojo/Lynx";

@TypeManagerDecorator("s25-lynx")
@Component({
    selector: "s25-lynx",
    template: `
        @if (init) {
            <div class="c-margin-left--single">
                @if (hasInstances) {
                    <div>
                        <label class="c-margin-top--single ngBold"> Pause Section </label>
                        <s25-toggle-button [(value)]="paused" (valueChange)="setState($event)"></s25-toggle-button>
                        <p class="ngFinePrint">
                            When a section is part of an active import or export extract set, pausing prevents it from
                            updating in 25Live when it is changed in your Student Information System (SIS) and prevents
                            it from updating in your SIS when it is changed in 25Live.
                        </p>
                        <p class="ngFinePrint ngBold">
                            If you un-pause this section, you must re-import it via LYNX to ensure that 25Live reflects
                            what is in your SIS.
                        </p>
                        @if (statusMessage) {
                            <p class="ngFinePrint ngBold">{{ statusMessage }}</p>
                        }
                        @if (affectedEvents.length) {
                            <div class="lynx-sync-results">
                                <label class="ngBold">Linked events updated</label>
                                <table class="lynx-sync-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Locator</th>
                                            <th>Result</th>
                                            <th>Triggered by binding</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        @for (affectedEvent of affectedEvents; track affectedEvent.event_id) {
                                            <tr>
                                                <td>
                                                    {{ affectedEvent.event_name || affectedEvent.event_title || "-" }}
                                                </td>
                                                <td>{{ affectedEvent.event_locator || "-" }}</td>
                                                <td>{{ formatOutcome(affectedEvent) }}</td>
                                                <td>{{ formatTriggerBinding(affectedEvent) }}</td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>
                        }
                    </div>
                }
                @if (!hasInstances) {
                    <div>
                        <p>There are no LYNX instances associated with the current user.</p>
                    </div>
                }
            </div>
        }
    `,
    styles: [
        "p.ngFinePrint {margin-top: 1.5em; max-width:500px;} div.toggle-wrapper {height: auto;} .lynx-sync-results { margin-top: 1rem; max-width: 900px; } .lynx-sync-table { width: 100%; border-collapse: collapse; } .lynx-sync-table th, .lynx-sync-table td { padding: 0.5rem; text-align: left; vertical-align: top; }",
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25LynxComponent implements OnInit {
    @Input() itemId: number;

    alienUid: string;
    init: boolean = false;
    paused: boolean;
    hasInstances: boolean;
    multiInstances: boolean;
    statusMessage: string;
    affectedEvents: LynxWs.LynxAffectedEvent[] = [];

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    setState(paused: boolean) {
        if (!paused) {
            return AlertService.confirm(
                "Reminder: " +
                    "If you un-pause this section, you must re-import it via LYNX to ensure that 25Live reflects what is in your SIS.",
            ).then((confirmUnpause) => {
                if (confirmUnpause) {
                    TelemetryService.send("LYNXTab", "Unpause");
                    return this.updateState(paused);
                }
                this.paused = true;
                this.cd.detectChanges();
            });
        } else {
            TelemetryService.send("LYNXTab", "Pause");
            return this.updateState(paused);
        }
    }

    private updateState(paused: boolean) {
        this.statusMessage = "";
        return LynxService.setState(this.alienUid, paused).then(
            (statusDoc) => {
                this.affectedEvents = statusDoc?.affected_events || [];
                const syncedCount = this.affectedEvents.filter(
                    (affectedEvent) => affectedEvent.outcome === "synced",
                ).length;
                const skippedCount = this.affectedEvents.filter(
                    (affectedEvent) => affectedEvent.outcome === "skippedNonLynx",
                ).length;
                this.statusMessage = `${paused ? "Paused" : "Unpaused"} event sync. ${syncedCount} bound event(s) synced${skippedCount ? `, ${skippedCount} non-LYNX event(s) skipped` : ""}.`;
                this.cd.detectChanges();
            },
            (error) => {
                this.paused = !paused;
                this.cd.detectChanges();
                S25Util.showError(error);
            },
        );
    }

    formatOutcome(affectedEvent: LynxWs.LynxAffectedEvent): string {
        if (affectedEvent.outcome === "synced") {
            return "Synced";
        }
        if (affectedEvent.outcome === "skippedNonLynx") {
            return affectedEvent.message || "Skipped (non-LYNX)";
        }
        return affectedEvent.message || "Error";
    }

    formatTriggerBinding(affectedEvent: LynxWs.LynxAffectedEvent): string {
        const triggerBinding = affectedEvent.trigger_binding;
        if (!triggerBinding) {
            return "Selected event";
        }
        const fromProfile = triggerBinding.from_profile_name || triggerBinding.from_profile_id || "?";
        const toProfile = triggerBinding.to_profile_name || triggerBinding.to_profile_id || "?";
        return `${triggerBinding.from_event_locator || triggerBinding.from_event_id || "?"} profile ${fromProfile} -> ${triggerBinding.to_event_locator || triggerBinding.to_event_id || "?"} profile ${toProfile}`;
    }

    ngOnInit() {
        return EventService.getAlienUid(this.itemId).then((alienUid) => {
            this.alienUid = alienUid;
            return S25Util.all({
                instances: LynxService.getUserInstances(),
                paused: LynxService.getSingleState(this.alienUid),
            }).then(
                (resp) => {
                    this.hasInstances = resp.instances.length > 0;
                    this.multiInstances = resp.instances.length > 1;
                    this.paused = !!resp.paused;
                    this.init = true;
                    this.cd.detectChanges();
                },
                (error) => {
                    S25Util.showError(error);
                },
            );
        });
    }
}
